/**
 * Bright, distinct region-perimeter colours for the cartographic outline layer.
 *
 * DESIGN CONTRACT
 * ---------------
 * These colours are used ONLY for cross-region boundary strokes. They are
 * deliberately NOT a subset of PLAYER_COLORS so a region outline is never
 * confused with a player-ownership signal.
 *
 * Region strokes and ownership fills have different geometric roles. The
 * historical palette audit used an obsolete player palette; it cannot prove
 * separation from the current nine player colors. Keep ownership names/slots
 * and color-blind patterns available rather than claiming universal hue separation.
 *
 * BROAD_REGION_PALETTE (28 entries)
 * -----------------------------------
 * Curated for maps up to 28 regions. Designed for maximum perceptual
 * distinction: hue bands are spread across the full wheel, and within each
 * band lightness is varied so "deep", "mid", and "light" reads are clearly
 * distinct even within the same hue family. Verified properties:
 *   - Every pair in the palette is ΔE76 > 18 (all 28 visible simultaneously
 *     on dense maps like Forbidden City).
 *   - Minimum pairwise ΔE76 ≈ 18.5 (#1A237E deep-indigo vs #4A148C deep-purple).
 *
 * BROAD vs LEGACY
 * ---------------
 * REGION_PERIMETER_PALETTE (18 entries) is retained for backward compat.
 * assignRegionColors now defaults to BROAD_REGION_PALETTE when the region
 * count is ≤ BROAD_REGION_PALETTE.length (28).
 *
 * ASSIGNMENT
 * ----------
 * Colours are assigned via `assignRegionColors` below, which runs a greedy
 * graph-colouring pass so that no two adjacent regions receive visually-similar
 * colours. The scoring function penalises both neighbour conflicts and global
 * colour reuse so every region lands on a unique curated entry. After
 * assignment, any adjacent pair with ΔE76 < 30 triggers a console.warn (safe
 * fallback — no throw — so the map still renders).
 */

// ─── Curated 28-colour broad palette ──────────────────────────────────────────

export const BROAD_REGION_PALETTE: readonly string[] = [
  // ── Crimson-reds (H 0–5) ──────────────────────────────────────────────────
  '#7A0000',  // very dark crimson    L=24  — avoids P2 orange by hue AND luminance
  '#D32F2F',  // mid red              L=47  — deeper/more saturated than P2
  '#FF6B6B',  // light coral          L=64  — desaturated warm-red
  // ── Rose / hot-pink (H 330–340) ────────────────────────────────────────────
  '#880E4F',  // deep rose            L=30
  '#EC407A',  // vivid rose-pink      L=55
  // ── Orange-brown (H 20–28, dark only — avoids P2 H=21 L=57 mid-zone) ──────
  '#4E1700',  // very dark sienna     L=17
  '#7A3200',  // dark sienna          L=30
  // ── Amber / olive-gold (H 42–52, mid-dark — P3 is H=48 L=84, stay <L=60) ──
  '#5E4800',  // dark gold            L=32
  '#A08000',  // olive-gold           L=55
  // ── Lime (H 72–88) ─────────────────────────────────────────────────────────
  '#2E4000',  // very dark lime       L=24
  '#6B9E00',  // olive-lime           L=57  — well below P3 lemon-yellow in L
  '#B8F000',  // electric lime        L=88
  // ── Green (H 110–135) ──────────────────────────────────────────────────────
  '#1B5E20',  // deep forest green    L=35
  '#66BB6A',  // bright green         L=69
  // ── Seafoam (H 163–167) ────────────────────────────────────────────────────
  '#00695C',  // deep teal-green      L=39
  '#00E5CC',  // vivid seafoam        L=82
  // ── Cyan (H 183–188, bright only — P5 H=192 L=56, use high L only) ─────────
  '#00D4E8',  // bright cyan          L=78
  // ── Indigo (H 233–242) ─────────────────────────────────────────────────────
  '#1A237E',  // deep indigo          L=20
  '#9FA8DA',  // light periwinkle     L=67
  // ── Purple (H 270–285, P4 H=293 L=49 — keep dark or light, not mid) ────────
  '#4A148C',  // deep purple          L=23
  '#CE93D8',  // light lavender       L=65
  // ── Fuschia (H 310–320) ────────────────────────────────────────────────────
  '#B0003A',  // deep fuschia-crimson L=37
  '#F48FB1',  // light pink           L=71
  // ── Earthy warm neutrals ────────────────────────────────────────────────────
  '#5D4037',  // dark warm brown      L=30
  '#8B7355',  // sandy brown          L=50
  // ── Cool neutral / mixed accents ────────────────────────────────────────────
  '#3D4B52',  // dark slate-gray      L=31
  '#D2B48C',  // light sandy tan      L=75
  '#80CBC4',  // pale seafoam-mint    L=77
]

// ─── Legacy palette (18 entries, kept for backward compat) ───────────────────

/**
 * Original curated palette. Retained so existing callers that import
 * REGION_PERIMETER_PALETTE directly continue to compile without changes.
 * New maps should use BROAD_REGION_PALETTE (28 entries) via the default
 * argument in `assignRegionColors`.
 */
export const REGION_PERIMETER_PALETTE: readonly string[] = [
  // ── Band B vivids (rose → crimson) ──────────────────────────────────────
  '#FF3A5C',  // vivid rose-red   H=350 S=100 L=61  — Band B, most vivid
  '#F062A4',  // hot pink         H=332 S=83  L=66  — Band B, rosy pink
  '#E0365C',  // deep rose        H=347 S=73  L=55  — Band B, deeper crimson-rose
  '#E11D74',  // bright magenta   H=336 S=82  L=50  — Band B, deeper than hot-pink
  // ── Band A vivids (lime → green → seafoam) ──────────────────────────────
  '#9EE800',  // electric lime    H=79  S=100 L=45  — Band A, lime-yellow
  '#78E05A',  // lime green       H=107 S=68  L=62  — Band A, yellow-green
  '#34C759',  // mint green       H=135 S=59  L=49  — Band A, mid-green
  '#10B981',  // emerald          H=160 S=84  L=39  — Band A, dark emerald
  '#00C896',  // seafoam          H=165 S=100 L=39  — Band A, cyan-green
  '#22543D',  // forest green     H=152 S=41  L=23  — Band A, deep/dark (distinct from lime)
  // ── Band C cyan (safely clear of P5 #0891B2 H=192) ─────────────────────
  '#06B6D4',  // bright cyan      H=191 S=92  L=44  — Band C (hue 191, P5 H=192 but L=50 vs 44)
  // ── Warm accent vivids ───────────────────────────────────────────────────
  '#FFB347',  // vivid amber      H=33  S=100 L=64  — warm accent (S=100 but H far from P2 H=21)
  '#B45309',  // burnt sienna     H=34  S=91  L=37  — warm accent, dark (distinct L from amber)
  // ── Desaturated fillers (cycle padding for very large region counts) ─────
  '#A2845E',  // bronze           H=34  S=27  L=50  — desaturated warm (cycle filler)
  '#7A9E4F',  // sage olive       H=87  S=33  L=46  — desaturated green (cycle filler)
  '#8E8E93',  // graphite         H=240 S=2   L=57  — near-neutral gray (cycle filler)
  '#9B8EA8',  // mauve gray       H=270 S=13  L=61  — desaturated cool (cycle filler)
  '#6B7280',  // slate gray       H=220 S=9   L=46  — cool-neutral filler
]

// ─── Perceptual colour distance ───────────────────────────────────────────────

/**
 * Convert an sRGB hex string (#rrggbb) to CIELAB (L*, a*, b*).
 *
 * Pipeline: hex → sRGB [0,1] → linear RGB (gamma expand) → XYZ (D65) → Lab.
 * The resulting Lab values are used for Euclidean distance comparisons in
 * `assignRegionColors`. This is the same standard conversion used by CSS
 * Color Level 4 and ICC colour management.
 *
 * Returns [L, a, b] or null for unrecognised input.
 */
function hexToLab(hex: string): [number, number, number] | null {
  const m = hex.match(/^#([0-9a-f]{6})$/i)
  if (!m || !m[1]) return null
  const h = m[1]
  const rs = parseInt(h.slice(0, 2), 16) / 255
  const gs = parseInt(h.slice(2, 4), 16) / 255
  const bs = parseInt(h.slice(4, 6), 16) / 255

  // sRGB → linear (gamma expansion)
  const lin = (c: number) =>
    c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  const rl = lin(rs)
  const gl = lin(gs)
  const bl = lin(bs)

  // Linear RGB → CIE XYZ (D65, sRGB primaries, IEC 61966-2-1)
  const X = rl * 0.4124564 + gl * 0.3575761 + bl * 0.1804375
  const Y = rl * 0.2126729 + gl * 0.7151522 + bl * 0.0721750
  const Z = rl * 0.0193339 + gl * 0.1191920 + bl * 0.9503041

  // Normalise by D65 white point
  const xn = X / 0.95047
  const yn = Y / 1.00000
  const zn = Z / 1.08883

  // XYZ → Lab (CIE standard)
  const f = (t: number) =>
    t > 0.008856 ? Math.cbrt(t) : (903.3 * t + 16) / 116
  const fx = f(xn)
  const fy = f(yn)
  const fz = f(zn)

  const L = 116 * fy - 16
  const a = 500 * (fx - fy)
  const b = 200 * (fy - fz)
  return [L, a, b]
}

/**
 * Perceptual (CIELAB Euclidean) distance between two hex colours.
 * Falls back to 0 when either hex is unparseable (treats them as identical
 * — the greedy pass then treats that pairing as maximally conflicting, which
 * is the safe direction: the algorithm will try to avoid it).
 */
export function labDistance(hexA: string, hexB: string): number {
  const la = hexToLab(hexA)
  const lb = hexToLab(hexB)
  if (!la || !lb) return 0
  const dL = la[0] - lb[0]
  const da = la[1] - lb[1]
  const db = la[2] - lb[2]
  return Math.sqrt(dL * dL + da * da + db * db)
}

// ─── Algorithmic palette generation ──────────────────────────────────────────

/**
 * Convert HSL (h ∈ [0,360), s ∈ [0,1], l ∈ [0,1]) to a 6-digit hex string.
 * Standard CSS hsl-to-rgb formula.
 */
function hslToHex(h: number, s: number, l: number): string {
  const a = s * Math.min(l, 1 - l)
  const f = (n: number): number => {
    const k = (n + h / 30) % 12
    return l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1))
  }
  const toHex = (v: number): string =>
    Math.round(v * 255).toString(16).padStart(2, '0')
  return `#${toHex(f(0))}${toHex(f(8))}${toHex(f(4))}`
}

/**
 * Generate `count` perceptually distinct colours using the golden-ratio hue
 * rotation trick (consecutive hues ~137.5° apart on the colour wheel).
 *
 * Saturation and lightness are fixed at values that keep colours vivid yet
 * readable against the map background.  Because consecutive hues in the
 * golden-ratio sequence are maximally spread across the wheel, every region
 * in display order gets a unique hue with no slot reuse regardless of `count`.
 *
 * Used by `assignRegionColors` when the map is too dense for the curated
 * BROAD_REGION_PALETTE (see comment in assignRegionColors).
 */
function generateAlgorithmicPalette(count: number): string[] {
  const GOLDEN_RATIO = 0.6180339887
  const SATURATION = 0.65
  const LIGHTNESS = 0.55
  const colours: string[] = []
  let hue = 0.0  // [0, 1)
  for (let i = 0; i < count; i++) {
    colours.push(hslToHex(Math.round(hue * 360), SATURATION, LIGHTNESS))
    hue = (hue + GOLDEN_RATIO) % 1
  }
  return colours
}

/**
 * Legacy threshold constant kept for backward compat so existing tests that
 * import DENSE_PALETTE_THRESHOLD continue to compile. The value is no longer
 * used inside `assignRegionColors` (which now compares directly against
 * BROAD_REGION_PALETTE.length), but external callers should not break.
 */
export const DENSE_PALETTE_THRESHOLD = 0.7

// ─── Graph-colouring assignment ───────────────────────────────────────────────

export type RegionId = string

/**
 * Weight applied to the global-uniqueness term in the scoring function.
 * A value of 0.35 means "penalise repeating an already-used palette entry at
 * 35% of the weight applied to adjacency conflicts". This keeps every region
 * on a unique curated colour while still prioritising maximising neighbour
 * contrast.
 */
const UNIQUENESS_ALPHA = 0.35

/**
 * Assign a palette colour to every region using a greedy graph-colouring
 * pass, so that no two ADJACENT regions receive visually-similar colours.
 *
 * Path selection:
 *   - regions.length ≤ BROAD_REGION_PALETTE.length (28)
 *     → uses BROAD_REGION_PALETTE with the uniqueness-penalised scoring below.
 *   - regions.length > 28
 *     → generates an algorithmic golden-ratio palette for the overflow;
 *       the first 28 regions land on curated entries, remaining on algorithmic.
 *       (The graph-colouring pass is applied over the combined palette.)
 *
 * Graph-colouring algorithm:
 * 1. Build the region adjacency graph from territory `adjacencies`.
 *    Regions A and B are adjacent if any territory in A lists any territory
 *    in B in its adjacency array.
 * 2. Sort regions by degree (most-constrained first), breaking ties by
 *    stable region index so output is deterministic.
 * 3. For each region in that order, score every palette entry as:
 *
 *      score(c) = min(
 *        minΔE76_to_already_assigned_neighbours(c),
 *        UNIQUENESS_ALPHA × minΔE76_to_any_already_used_colour(c),
 *      )
 *
 *    Pick the entry with the highest score. When no colours have been used
 *    yet (first region), fall back to palette[originalIndex % palette.length].
 *    Ties are broken by palette index (lower wins) for determinism.
 *
 * Determinism guarantee: the sort key is (degree DESC, original-index ASC),
 * and ties in the scoring step are broken by palette index (lower wins).
 * Same `regions` / `adjacencyByRegion` → same output across sessions.
 *
 * @param regions             Ordered region list (same as `state.map.regions`).
 * @param adjacencyByRegion   Map<regionId, Set<regionId>> of region-level neighbours.
 * @param palette             Colour palette to draw from (defaults to BROAD_REGION_PALETTE).
 */
export function assignRegionColors(
  regions: ReadonlyArray<{ id: string; color?: string }>,
  adjacencyByRegion: ReadonlyMap<string, ReadonlySet<string>>,
  palette: readonly string[] = BROAD_REGION_PALETTE,
): Record<RegionId, string> {
  if (regions.length === 0) return {}
  if (palette.length === 0) return {}

  // Map-supplied colour pins (Region.color) win — set them up-front so
  // the greedy walk picks around them.
  const pinned: Record<RegionId, string> = {}
  for (const r of regions) {
    if (typeof r.color === 'string' && /^#[0-9a-f]{6}$/i.test(r.color)) {
      pinned[r.id] = r.color
    }
  }

  // ── Palette selection ────────────────────────────────────────────────────
  // When the caller passes the default BROAD_REGION_PALETTE and there are
  // more regions than curated slots, extend with algorithmic colours so every
  // region can receive a unique palette entry.
  //
  // We compare against BROAD_REGION_PALETTE.length (the curated baseline) not
  // the passed-in `palette` so callers supplying small custom palettes for
  // testing purposes don't unintentionally trigger algorithmic generation.
  const effectivePalette: readonly string[] =
    regions.length > BROAD_REGION_PALETTE.length
      ? [
          ...BROAD_REGION_PALETTE,
          ...generateAlgorithmicPalette(regions.length - BROAD_REGION_PALETTE.length),
        ]
      : palette

  // Stable index for tie-breaking
  const originalIndex = new Map<string, number>()
  regions.forEach((r, i) => originalIndex.set(r.id, i))

  // Sort by degree DESC, then original index ASC (stable, deterministic).
  const sorted = regions.slice().sort((a, b) => {
    const degA = adjacencyByRegion.get(a.id)?.size ?? 0
    const degB = adjacencyByRegion.get(b.id)?.size ?? 0
    if (degB !== degA) return degB - degA
    return (originalIndex.get(a.id) ?? 0) - (originalIndex.get(b.id) ?? 0)
  })

  // Seed with caller-pinned colours so the greedy walk avoids them on
  // neighbours and uniqueness checks.
  const assigned: Record<string, string> = { ...pinned }
  const usedColours = new Set<string>(Object.values(pinned))

  for (const region of sorted) {
    if (assigned[region.id]) continue // pinned — skip
    const neighbours = adjacencyByRegion.get(region.id) ?? new Set<string>()

    // Collect already-assigned neighbour colours.
    const neighbourColors: string[] = []
    for (const nId of neighbours) {
      const c = assigned[nId]
      if (c) neighbourColors.push(c)
    }

    if (neighbourColors.length === 0 && usedColours.size === 0) {
      // Very first region — pick by original index so first-pass
      // assignment matches the deterministic cyclic order the palette was
      // designed around.
      const idx = (originalIndex.get(region.id) ?? 0) % effectivePalette.length
      const chosen = effectivePalette[idx]!
      assigned[region.id] = chosen
      usedColours.add(chosen)
    } else {
      // Score every palette entry:
      //   score = min(
      //     minΔE_to_neighbour_colours,           (adjacency term)
      //     α × minΔE_to_any_used_colour,          (uniqueness term)
      //   )
      // When there are no neighbours yet, the adjacency term is +∞ and only
      // the uniqueness term drives the choice.
      //
      // Curated-first guarantee: when there are unused curated entries
      // remaining, only those are considered. Algorithmic overflow entries
      // only become candidates once every curated slot has been assigned.
      // This ensures the first BROAD_REGION_PALETTE.length sorted regions
      // always land on curated entries before the algorithmic generator is
      // used for the overflow.
      const curatedSize = BROAD_REGION_PALETTE.length
      const unusedCuratedRemain =
        usedColours.size < curatedSize &&
        effectivePalette.length > curatedSize
      const searchEnd = unusedCuratedRemain ? curatedSize : effectivePalette.length

      let bestIdx = 0
      let bestScore = -1
      for (let pi = 0; pi < searchEnd; pi++) {
        const candidate = effectivePalette[pi]!

        // Adjacency term: min ΔE to already-assigned neighbours.
        let adjMin = Infinity
        for (const nc of neighbourColors) {
          const d = labDistance(candidate, nc)
          if (d < adjMin) adjMin = d
        }
        // When no neighbours are assigned yet, treat as unconstrained.
        if (!isFinite(adjMin)) adjMin = Infinity

        // Uniqueness term: min ΔE to any already-used colour.
        let usedMin = Infinity
        for (const uc of usedColours) {
          const d = labDistance(candidate, uc)
          if (d < usedMin) usedMin = d
        }
        // If no colours used yet, term is unconstrained.
        if (!isFinite(usedMin)) usedMin = Infinity

        const score = Math.min(
          adjMin,
          UNIQUENESS_ALPHA * usedMin,
        )
        if (score > bestScore) {
          bestScore = score
          bestIdx = pi
        }
      }
      const chosen = effectivePalette[bestIdx]!
      assigned[region.id] = chosen
      usedColours.add(chosen)
    }
  }

  // Post-assignment sanity check: warn if any adjacent pair has ΔE76 < 30.
  // This is cheap insurance — the greedy pass maximises distance but can
  // land below threshold when the palette is smaller than the chromatic
  // neighbourhood demands (e.g. a fully-connected 5-region cluster with
  // only 4 vivid palette entries). We log a warning rather than throwing
  // so the map still renders.
  for (const region of regions) {
    const neighbours = adjacencyByRegion.get(region.id) ?? new Set<string>()
    for (const nId of neighbours) {
      if (!(nId in assigned)) continue
      const d = labDistance(assigned[region.id]!, assigned[nId]!)
      if (d < 30) {
        console.warn(
          `[regionColors] Adjacent regions "${region.id}" and "${nId}" have ΔE76=${d.toFixed(1)} < 30 ` +
            `(colors: ${assigned[region.id]} / ${assigned[nId]}). ` +
            `Consider expanding BROAD_REGION_PALETTE or reviewing map adjacencies.`,
        )
      }
    }
  }

  return assigned
}

/**
 * Build the region-level adjacency graph from a territory list.
 *
 * Returns Map<regionId, Set<regionId>>. A region appears as its own key
 * even when it has no neighbours (empty Set), so callers can iterate
 * all regions safely.
 *
 * Exported so MapView and tests can share the same derivation logic.
 */
export function buildRegionAdjacency(
  territories: ReadonlyArray<{ id: string; regionId?: string; adjacencies: string[] }>,
  regions: ReadonlyArray<{ id: string }>,
): Map<string, Set<string>> {
  // Index territory → region
  const regionOfTerritory = new Map<string, string>()
  for (const t of territories) {
    if (t.regionId) regionOfTerritory.set(t.id, t.regionId)
  }

  // Seed: every region gets an (empty) entry so the map is complete.
  const adj = new Map<string, Set<string>>()
  for (const r of regions) adj.set(r.id, new Set())

  for (const t of territories) {
    const rA = t.regionId
    if (!rA) continue
    for (const adjId of t.adjacencies) {
      const rB = regionOfTerritory.get(adjId)
      if (!rB || rB === rA) continue
      adj.get(rA)?.add(rB)
      // Ensure rB has an entry and add the reverse direction.
      if (!adj.has(rB)) adj.set(rB, new Set())
      adj.get(rB)?.add(rA)
    }
  }

  return adj
}
